import Color from 'color'
import stringToColor from 'string-to-color'

import { rawTheme } from 'src/styles/raw'
import { resolveColorString } from 'src/utils/color'
import findClosestColorMatch from 'src/utils/findClosestColorMatch'

// Real hex, not `var(--wh-*)`: these feed `Color()` math and
// `findClosestColorMatch`, which parse the string and choke on `var()`
// (PRODUCT-154). Resolves light — avatar palettes go theme-aware in Phase 4.
const c = rawTheme('light')

export type AvatarColorType =
  | 'red'
  | 'green'
  | 'blue'
  | 'purple'
  | 'yellow'
  | 'gray'
  | 'orange'
  | 'cyan'

const AVATAR_COLORS = {
  red: c.red600,
  green: c.green600,
  blue: c.blue600,
  purple: c.purple600,
  yellow: c.yellow600,
  gray: c.gray700,
  orange: c.orange600,
  cyan: c.cyan600,
}

export function checkIsPresetColor(
  color: AvatarColorType | string
): color is AvatarColorType {
  const isPreset = [
    'red',
    'green',
    'blue',
    'purple',
    'yellow',
    'orange',
    'cyan',
    'gray',
  ].includes(color)

  // Only a preset NAME is a preset. The old body also ran `Color(color)` and
  // returned `!col` — which is `false` for every color the package parses, and
  // `true` from the catch. So an UNPARSEABLE value claimed to be a preset,
  // `AVATAR_COLORS[…]` yielded `undefined`, and `Color(undefined)` is black:
  // every avatar carrying a bad stored color rendered black-on-black initials.
  return isPreset
}

/**
 * The real color an avatar paints with.
 *
 * `color` is free-form persisted data (`user.color`, `depot.color`,
 * `project.color`, …) written by the color picker, an import, or an older
 * build — so it can be a preset name, a hex, legacy free text, or a
 * `var(--wh-*)` token (PRODUCT-154). `generateColorStyles` runs `Color()` maths
 * on the result inside a styled-components interpolation, so anything it can't
 * parse must be replaced HERE rather than silently becoming black.
 */
export function getBaseColor(color?: AvatarColorType | string, text?: string) {
  const fromText = () =>
    findClosestColorMatch(
      stringToColor(text || ''),
      Object.values(AVATAR_COLORS)
    )

  if (!color) {
    return fromText()
  }

  if (checkIsPresetColor(color)) {
    return AVATAR_COLORS[color]
  }

  // A `var()` token only means something to CSS; resolve it before parsing.
  const resolved = resolveColorString(color)
  try {
    Color(resolved)
    return resolved
  } catch {
    // Unparseable stored color — fall back to the same name-hashed color an
    // avatar with no color at all gets, so the initials stay legible.
    return fromText()
  }
}

export function generateColorStyles(
  color?: AvatarColorType | string,
  text?: string
) {
  const modifiedColor = getBaseColor(color, text)
  return {
    color: Color(modifiedColor).darken(0.6).hex(),
    backgroundColor: Color(modifiedColor).lighten(0.7).hex(),
  }
}
